import { Button } from "primereact/button";
import React, { useEffect, useState } from "react";
import ChartInDialogButton from "components/basic/chart-in-dialog-button/ChartInDialogButton";
import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import { T } from "services/lang/LanguageService";
import { Dialog } from "primereact/dialog";
import { ChronoUnitLegacy } from "utils/time-utils";
import { TitleOverrideAndDescriptionTemplate } from "components/templates/DataTableCellTemplates";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import DatasetLineChart from "../charts/dataset-line-chart/DatasetLineChart";
import DataInputOutputUpdateTable from "../data-input-output-table/update-dataset-table/DataInputOutputUpdateTable";
import { ICombinedData } from "../data-input-output-table/common/api/interfaces/interfaces";
import { IDataSet, IPIDControllerProps } from "../charts/interfaces";

interface DataToDisplayObject {
  value: string | undefined;
  unit: string | undefined;
}

type FetchDataProps = {
  [K in keyof IPIDControllerProps]: DataToDisplayObject | undefined;
};

interface IPIBFetchData extends FetchDataProps {
  [key: string]: DataToDisplayObject | undefined;
}

interface IdItem {
  key: PIDControllerKeys;
  id: string;
  decimals: number;
}

type PIDControllerKeys = keyof IPIDControllerProps;

const PIBController = (props: IPIDControllerProps) => {
  const [addDataDialogVisibility, setAddDataDialogVisibility] = useState(false);
  const [chartDialogVisibility, setChartDialogVisibility] = useState(false);
  const [currentFetchedData, setCurrentFetchedData] = useState<IPIBFetchData | undefined>(
    undefined
  );

  const useGetRecentDataArray: string[] = [
    props.controlVariable,
    props.kp,
    props.ki,
    props.kd,
    props.setPoint,
    props.manipulatedVariable,
    props.min,
    props.max,
    props.delta,
    props.kall,
  ];

  const { data: recentDatasetData, refetch } = useGetRecentData(useGetRecentDataArray, {
    count: 1,
    options: {
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 10000,
    },
  });

  useEffect(() => {
    if (recentDatasetData) {
      updateData(recentDatasetData);
    }
  }, [recentDatasetData]);

  const updateData = (data: IDataSet[]) => {
    const getValueAndUnit = (id: string) => {
      const dataset = data.find(d => id === d.identifier);
      return {
        value: dataset?.data[0]?.value,
        unit: dataset?.unit,
      };
    };

    const ids: IdItem[] = [
      { key: "kp", id: props.kp, decimals: 2 },
      { key: "ki", id: props.ki, decimals: 2 },
      { key: "kd", id: props.kd, decimals: 2 },
      { key: "kall", id: props.kall, decimals: 2 },
      { key: "setPoint", id: props.setPoint, decimals: 2 },
      { key: "controlVariable", id: props.controlVariable, decimals: 2 },
      { key: "manipulatedVariable", id: props.manipulatedVariable, decimals: 2 },
      { key: "max", id: props.max, decimals: 0 },
      { key: "min", id: props.min, decimals: 0 },
      { key: "delta", id: props.delta, decimals: 0 },
    ];

    const fetchedData: IPIBFetchData = ids.reduce((acc, { key, id, decimals }) => {
      const { value, unit } = getValueAndUnit(id);
      acc[key] = value !== undefined ? { value: value.toFixed(decimals), unit } : undefined;
      return acc;
    }, {} as IPIBFetchData);

    setCurrentFetchedData(fetchedData);
  };

  const titleColumnRender = (rowData: ICombinedData) => {
    return (
      <div className="flex gap-2">
        <TitleOverrideAndDescriptionTemplate
          data={{
            title: rowData.title,
            overriddenTitle: rowData.overriddenTitle,
            description: rowData.description,
            identifier: rowData.identifier,
          }}
        />
        <ChartInDialogButton identifier={rowData.identifier} />
      </div>
    );
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        easasAlert({
          summary: T("alert.pid-controller.identifier-copied-to-clipboard", {text}),
          severity: "success",
        });
      },
      err => {
        easasAlert({
          summary: T("alert.pid-controller.could-not-copy-identifier"),
          severity: "error",
          detail: err,
        });
      }
    );
  };

  const handleDoubleClick = (identifier: string) => {
    copyToClipboard(identifier);
  };

  const datasets = recentDatasetData || [];

  return (
    <div className="flex flex-column md:flex-row justify-content-between">
      <div className="w-3 flex flex-column justify-content-between">
        <div className="w-full">
          <table className="w-full">
            <tbody className="flex flex-column gap-2 border-400 border-1 p-2 border-round-xl mb-4 shadow-1">
              <tr>
                <td className="w-7rem text-lg" onDoubleClick={() => handleDoubleClick(props.kp)}>
                  <b>
                    K<sub>P</sub>: <br />
                  </b>{" "}
                  {currentFetchedData && currentFetchedData.kp
                    ? currentFetchedData.kp.value
                    : T("message.pid-controller.value-is-undefined")}{" "}
                  {currentFetchedData && currentFetchedData.kp?.unit}
                </td>
                <td className="pl-3" onDoubleClick={() => handleDoubleClick(props.controlVariable)}>
                  <b>CV: </b> <br />
                  {currentFetchedData && currentFetchedData.controlVariable
                    ? `${currentFetchedData.controlVariable.value} ${currentFetchedData.controlVariable.unit}`
                    : T("message.pid-controller.value-is-undefined")}{" "}
                  <ChartInDialogButton identifier={props.controlVariable as string} />
                </td>
              </tr>
              <tr>
                <td className="w-7rem" onDoubleClick={() => handleDoubleClick(props.ki)}>
                  <b>
                    K<sub>I</sub>:<br />
                  </b>{" "}
                  {currentFetchedData && currentFetchedData.ki
                    ? currentFetchedData.ki.value
                    : T("message.pid-controller.value-is-undefined")}{" "}
                  {currentFetchedData && currentFetchedData.ki?.unit}
                </td>
                <td className="pl-3" onDoubleClick={() => handleDoubleClick(props.setPoint)}>
                  <b>SP:</b> <br />
                  {currentFetchedData && currentFetchedData.setPoint
                    ? `${currentFetchedData.setPoint.value} ${currentFetchedData.setPoint.unit}`
                    : T("message.pid-controller.value-is-undefined")}{" "}
                  <ChartInDialogButton identifier={props.setPoint as string} />
                </td>
              </tr>
              <tr>
                <td className="w-7rem" onDoubleClick={() => handleDoubleClick(props.kd)}>
                  <b>
                    K<sub>D</sub>:<br />
                  </b>{" "}
                  {currentFetchedData && currentFetchedData.kd
                    ? currentFetchedData.kd.value
                    : T("message.pid-controller.value-is-undefined")}
                  {currentFetchedData && currentFetchedData.kd?.unit}
                </td>
                <td
                  className="pl-3"
                  onDoubleClick={() => handleDoubleClick(props.manipulatedVariable)}
                >
                  <b>MV:</b> <br />
                  {currentFetchedData && currentFetchedData.manipulatedVariable
                    ? `${currentFetchedData.manipulatedVariable.value} ${currentFetchedData.manipulatedVariable.unit}`
                    : T("message.pid-controller.value-is-undefined")}{" "}
                  <ChartInDialogButton identifier={props.manipulatedVariable as string} />
                </td>
              </tr>
              <tr>
                <td className="w-7rem" onDoubleClick={() => handleDoubleClick(props.delta)}>
                  <b>Δ</b>: <br />
                  {currentFetchedData && currentFetchedData.delta
                    ? currentFetchedData.delta.value
                    : T("message.pid-controller.value-is-undefined")}{" "}
                  {currentFetchedData && currentFetchedData.delta?.unit}
                </td>
              </tr>
              <tr>
                <td className="w-7rem" onDoubleClick={() => handleDoubleClick(props.min)}>
                  <b>Min:</b> <br />
                  {currentFetchedData && currentFetchedData.min
                    ? currentFetchedData.min.value
                    : T("message.pid-controller.value-is-undefined")}{" "}
                  {currentFetchedData && currentFetchedData.min?.unit}
                </td>
              </tr>
              <tr>
                <td className="w-7rem" onDoubleClick={() => handleDoubleClick(props.max)}>
                  <b>Max:</b> <br />
                  {currentFetchedData && currentFetchedData.max
                    ? currentFetchedData.max.value
                    : T("message.pid-controller.value-is-undefined")}{" "}
                  {currentFetchedData && currentFetchedData.max?.unit}
                </td>
                <td className="pl-3" onDoubleClick={() => handleDoubleClick(props.kall)}>
                  <b>
                    K<sub>all</sub> :<br />
                  </b>{" "}
                  {currentFetchedData && currentFetchedData.kall
                    ? currentFetchedData.kall.value
                    : T("message.pid-controller.value-is-undefined")}{" "}
                  {currentFetchedData && currentFetchedData.kall?.unit}{" "}
                  <ChartInDialogButton identifier={props.kall as string} />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="flex gap-2 justify-content-between">
          <Button className="w-max" onClick={() => setAddDataDialogVisibility(true)}>
            {T("action.update")}
          </Button>
          <i
            className="pi pi-chart-line"
            onClick={() => {
              setChartDialogVisibility(true);
            }}
            style={{ cursor: "pointer", margin: "auto" }}
          />
        </div>
      </div>
      <div className="w-9 :w-2/3 mt-3 lg:mt-0">
        <DatasetLineChart
          identifiers={[props.controlVariable, props.setPoint, props.manipulatedVariable]}
          dynamicHeight
          showDateSelection={false}
          hideRefresh
          chartDialogOpen={false}
          setMinYAxis={
            currentFetchedData?.min?.value ? Number(currentFetchedData.min.value) : undefined
          }
          setMaxYAxis={
            currentFetchedData?.max?.value ? Number(currentFetchedData.max.value) : undefined
          }
          dateAndRefreshButtonsVisible={false}
          loadConf={props.loadConf}
        />
      </div>
      <Dialog
        breakpoints={{ "960px": "75vw", "640px": "100vw" }}
        style={{ width: "50vw" }}
        draggable={false}
        resizable={false}
        header={T("action.update-datasets")}
        visible={addDataDialogVisibility}
        onHide={() => {
          setAddDataDialogVisibility(false);
          refetch();
        }}
        blockScroll
      >
        <DataInputOutputUpdateTable
          titleColumnReader={titleColumnRender}
          identifiers={useGetRecentDataArray}
          firstLoadDate={new Date()}
          datasets={datasets}
          dateFrequency={ChronoUnitLegacy.MINUTES}
          closeDialog={() => setAddDataDialogVisibility(false)}
        />
      </Dialog>
      <Dialog
        breakpoints={{ "960px": "75vw", "640px": "100vw" }}
        style={{ width: "50vw" }}
        draggable={false}
        resizable={false}
        header={T("action.open-chart")}
        visible={chartDialogVisibility}
        onHide={() => setChartDialogVisibility(false)}
        blockScroll
      >
        <DatasetLineChart
          identifiers={[props.controlVariable, props.setPoint, props.manipulatedVariable]}
          showDateSelection
          setMinYAxis={
            currentFetchedData?.min?.value ? Number(currentFetchedData.min.value) : undefined
          }
          setMaxYAxis={
            currentFetchedData?.max?.value ? Number(currentFetchedData.max.value) : undefined
          }
          loadConf={props.loadConf}
        />
      </Dialog>
    </div>
  );
};

export default PIBController;
